import {api} from "@/shared/api";

import {DriveLogMap} from "@/features/drivelog/driveLogMap";
import {DriveLogDialog} from "@/features/drivelog/driveLogDialog";
import {DriveLogDetailsDialog} from "@/features/drivelog/driveLogDetailsDialog";
import {DriveLogTable} from "@/features/drivelog/driveLogTable";
import {RecordingReport, RecordingReportConfig} from "./recordingReport";
import {Combobox} from "@/shared/ui/combobox";
import {RecordingTable} from "@/shared/recording/recordingTable";
import {isDriveLogDateTimeChangeAllowed} from "@/shared/securityHelper";
import * as Constants from "@/shared/constants";
import {EMPTY_CALLBACK} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {parseISODate} from "@/shared/utils/datefunctions";
import {Car, DriveLogCategory, DriveLogUserSums, ReportDriveLog, User} from "@/shared/timr";
import {DriveLogReportingQueryParams} from "@/shared/queryParams";


declare const c3: typeof import("c3");

export interface DriveLogReportConfig extends RecordingReportConfig {
	cars: Car[];
	driveLogCategories: DriveLogCategory[];
}

interface DriveLogStatisticsData {
	driveLogSums: DriveLogUserSums;
}

export class DriveLogReportPage extends RecordingReport<ReportDriveLog> {

	private driveLogDialog: DriveLogDialog;
	private driveLogDetailsDialog: DriveLogDetailsDialog;
	declare private businessChart: c3.ChartAPI;
	declare private driveLogMap: DriveLogMap;
	declare private carCombobox: Combobox<Car>;

	constructor(public config: DriveLogReportConfig) {
		super(config, {
			formSelector: "#driveLogFilterForm",
			roundingFiltersSelector: "#driveLogRoundingFilters",
			filterButtonSelector: "#driveLogFilterButton",
			bulkOperationDropDownSelector: "#driveLogBulkOperationDropDown",
			bulkOperationButtonSelector: "#driveLogBulkOperationButton",
			exportDropDownSelector: "#driveLogExportDropDown",
			exportButtonSelector: "#driveLogExportButton",
			roundingModeSelector: "#driveLogRoundingMode",
			roundToMinutesSelector: "#driveLogRoundToMinutes",
			groupDropdownSelector: "#driveLogFilterGroupDropdown",
			userIdSelector: "#driveLogFilterUserId",
			userFilterSelector: "#driveLogFilterUserFilter",
			userCaretSelector: "#driveLogFilterUserCaret",
			showInactiveSelector: "#driveLogReportShowInactive",
			bulkOperationDeleteConfirmMessage: "drivelog.bulkdelete.message",
			bulkOperationDeleteUrl: "/reports/bulkDeleteDriveLogs.json",
			bulkOperationSetStatusUrl: "/reports/updateDriveLogStatus.json",
			exportBaseUrl: "export/driveLogReport.json",
			exportStatusBaseUrl: "export/status.json",
			exportRevisionBaseUrl: "export/driveLogRevisionsReport.json",
			accountExpiredTitle: "account.expired.drivelog.title",
			accountExpired: "account.expired.drivelog"
		});
	}

	protected override getReportingQueryParams() {
		return DriveLogReportingQueryParams;
	}

	protected override applyRecordTypeQueryParams(queryParams: typeof DriveLogReportingQueryParams, pendingHydrationRequests: PromiseLike<unknown>[]) {
		const appliedKeys = super.applyRecordTypeQueryParams(queryParams, pendingHydrationRequests);

		const carId = queryParams.get("carId");
		if (carId && this.carCombobox) {
			this.carCombobox.setSelectedItemById(carId, false, false);
			appliedKeys.push("carId");
		}
		return appliedKeys;
	}

	protected init() {
		super.init();

		this.carCombobox = new Combobox({
			items: this.config.cars,
			formatter: (car) => {
				return `${car.plate} - ${car.name}`;
			},
			idElement: $("#driveLogFilterCarId"),
			filterElement: $("#driveLogFilterCarFilter"),
			caretElement: $("#driveLogFilterCarCaret")
		});
		$(this.carCombobox).on(Combobox.EVENT_CHANGED, (e) => {
			this.filterTable();
		});

		$(this.recordingTable).on(DriveLogTable.EVENT_DATA_CHANGED, (e, data: DriveLogStatisticsData) => {
			this.updateStatistics(data);
		});

		$("#addDriveLogButton").click((e) => {
			e.preventDefault();
			this.showAddRecordDialog();
		}).toggleClass("disabled", !isDriveLogDateTimeChangeAllowed(true));

		this.initBusinessChart();

		this.driveLogMap = new DriveLogMap({
			container: $("#driveLogReportMapContainer")
		});
	}

	protected createRecordingTable(): RecordingTable<ReportDriveLog> {
		return new DriveLogTable({
			dataUrl: DriveLogTable.REPORT_DRIVE_LOG_URL,
			element: $("#driveLogTable"),
			initialFilter: this.buildFilterParams(),
			allowQuickSwitch: false,
			allowResume: false,
			allowSelect: true,
			customFieldDefinitions: this.config.customFieldDefinitions,
			statusNumbers: this.config.statusNumbers,
			allowedUserIds: this.config.allowedUserIds,
			showUser: true,
			orderable: true,
			clampNote: false,
			allowExpand: false,
			pageLength: Constants.REPORT_TABLES_PAGE_LENGTH,
			paging: true,
			excludeElementsDblclickEvent: [RecordingReport.EXCLUDE_ELEMENTS_DBLCLICK_EVENT]
		});
	}

	protected onShowInactiveChanged(showInactive: boolean) {
		super.onShowInactiveChanged(showInactive);
		this.reloadCarList(showInactive);
	}

	protected invalidateResult(source: Element) {
		if (source.id !== "driveLogFilterCarFilter") {
			super.filterTable();
		}
	}

	protected setUsers(users: User[]) {
		super.setUsers(users);
		if (this.driveLogDialog != null) {
			this.driveLogDialog.config.users = users;
		}
	}

	protected show(recording: ReportDriveLog) {
		this.getDriveLogDetailsDialog().show(recording);
	}

	protected edit(recording: ReportDriveLog) {
		this.getDriveLogDialog().edit(recording);
	}

	protected duplicate(recording: ReportDriveLog) {
		this.getDriveLogDialog().duplicate(recording);
	}

	protected onBulkSelectionChanged(recordings: ReportDriveLog[]) {
		super.onBulkSelectionChanged(recordings);

		// We only need to clear drive logs from map (e.g. if paging is changed)
		// because we don't get a bulkSelectionToggled event for that.
		// In all other cases (e.g. select all, select none, ...) bulkSelectionToggled
		// is called
		if (recordings.length === 0) {
			this.driveLogMap.clearDriveLogs();
		}
	}

	protected onBulkSelectionToggled(recording: ReportDriveLog, selected: boolean) {
		this.driveLogMap.toggleDriveLog(recording, selected);
	}

	private reloadCarList(showInactive: boolean) {
		api("reports/findCars.json").get({usableCarsOnly: !showInactive}).done((data) => {
			if (data && data.cars) {
				this.carCombobox.updateItems(data.cars);
			}
		});
	}

	private updateStatistics(data: DriveLogStatisticsData) {
		const driveLogSums = data.driveLogSums;
		let business = 0;
		let privates = 0;
		if (driveLogSums.businessSums) {
			business = driveLogSums.businessSums.distance;
		}
		if (driveLogSums.privateSums) {
			privates = driveLogSums.privateSums.distance;
		}
		const sum = business + privates;
		$("#driveLogBusinessDurationSum").text(business);
		$("#driveLogPrivateDurationSum").text(privates);
		$("#driveLogDurationSum").text(sum);
		$("#driveLogBusinessChart").toggle(sum > 0);
		this.businessChart.load({
			columns: this.buildBusinessChartColumns(business, privates, sum),
			names: this.buildBusinessChartNames(business, privates, sum)
		});
	}

	private initBusinessChart() {
		this.businessChart = c3.generate({
			bindto: "#driveLogBusinessChart",
			size: {width: 270, height: 70},
			data: {
				columns: this.buildBusinessChartColumns(0, 0, 0),
				type: "donut",
				names: this.buildBusinessChartNames(0, 0, 0),
			},
			legend: {
				item: {
					onclick: EMPTY_CALLBACK
				},
				position: "right"
			},
			donut: {
				label: {
					show: false
				},
				width: 7
			},
			interaction: {
				enabled: false
			}
		});
		$("#driveLogBusinessChart")
			.find(".c3-legend-item").attr("transform", "translate(6, 5)")
			.find(".c3-legend-item-tile").attr("transform", "translate(6, 1)");
	}

	private buildBusinessChartColumns(business: number, privates: number, sum: number): c3.PrimitiveArray[] {
		const businessPercent = sum > 0 ? business / sum : 0.5;
		const privatePercent = sum > 0 ? privates / sum : 0.5;
		return [
			["business", businessPercent],
			["privates", privatePercent]
		];
	}

	private buildBusinessChartNames(business: number, privates: number, sum: number): { [key: string]: string } {
		const businessPercent = sum > 0 ? business / sum : 0.5;
		const privatePercent = sum > 0 ? privates / sum : 0.5;
		return {
			business: this.formatPercent(businessPercent) + " " + messages["statisticsPanel.drivelog.business"],
			privates: this.formatPercent(privatePercent) + " " + messages["statisticsPanel.drivelog.private"]
		};
	}

	private createDriveLogDetailsDialog(): DriveLogDetailsDialog {
		return new DriveLogDetailsDialog({
			title: messages["driveLog.details.header"],
			dialogId: "#driveLogDetailsDialog",
			customFieldDefinitions: this.config.customFieldDefinitions,
			editRecord: (recording: ReportDriveLog) => this.edit(recording)
		});
	}

	private getDriveLogDetailsDialog(): DriveLogDetailsDialog {
		if (this.driveLogDetailsDialog == null) {
			this.driveLogDetailsDialog = this.createDriveLogDetailsDialog();
		}
		return this.driveLogDetailsDialog;
	}

	private createDriveLogDialog(): DriveLogDialog {
		const dialog = new DriveLogDialog({
			dialogId: "#driveLogDialog",
			users: this.users,
			cars: this.config.cars,
			driveLogCategories: this.config.driveLogCategories,
			userSelectionEnabled: globals.isGroupLeader,
			customFieldDefinitions: this.config.customFieldDefinitions,
			defaultStatusNumber: this.config.defaultStatusNumber,
			allowedUserIds: this.config.allowedUserIds,
			isGroupLeaderForThemself: this.config.isGroupLeaderForThemself,
			dateTimeChangeableByUserDayRangeUntil: parseISODate(this.config.dateTimeChangeableByUserDayRangeUntil),
			dateTimeChangeableByUserDayRangeMode: this.config.dateTimeChangeableByUserDayRangeMode
		});
		$(dialog).on(DriveLogDialog.EVENT_RECORD_EDITED, () => {
			this.reloadTable();
		}).on(DriveLogDialog.EVENT_RECORD_CREATED, () => {
			this.filterTable();
		}).on(DriveLogDialog.EVENT_RECORD_DELETED, () => {
			this.filterTable();
		});
		return dialog;
	}

	private getDriveLogDialog(): DriveLogDialog {
		if (this.driveLogDialog == null) {
			this.driveLogDialog = this.createDriveLogDialog();
		}
		return this.driveLogDialog;
	}

	protected getSecurityStatus() {
		return SecurityStatus.isAddDriveLogAllowed();
	}

	protected createAddRecordDialog(preSelectUser: User) {
		this.getDriveLogDialog().create(preSelectUser ? {userId: preSelectUser.id} : null, false);
	}

	protected getRecordById(recordId: number, link: JQuery) {
		// not implemented
	}

	protected createBulkMenu() {
		const menu = super.createBulkMenu();
		menu.addDivider();
		menu.addMenuEntry({
			label: messages["report.delete"],
			action: () => this.bulkOperationDelete(),
			show: true,
			active: true
		});

		return menu;
	}
}
